import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import useDialogFocus from '../../../hooks/useDialogFocus';

export default function Modal({ open, onClose, title, children, footer, maxWidth = 460 }) {
  // Focus-in, focus-restore, Tab trap and Escape-to-close all live in the
  // shared hook — the runner's InterruptPanel needs the same behaviour.
  const dialogRef = useDialogFocus({ active: open, onEscape: onClose });

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay-bg)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-overlay)',
          maxWidth,
          width: '100%',
          boxShadow: 'var(--shadow-lg)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          // No outline:none here — this panel is sometimes the actual focus
          // target (the fallback when no child grabs focus itself, e.g.
          // ConfirmDialog), so it needs a visible focus indicator, not a
          // suppressed one.
        }}
      >
        {title && (
          <div
            style={{
              padding: 'var(--space-4) var(--space-5) var(--space-3)',
              borderBottom: '1px solid var(--border-subtle)',
              fontSize: 'var(--text-panel-size)',
              fontWeight: 'var(--text-panel-weight)',
              color: 'var(--text-primary)',
            }}
          >
            {title}
          </div>
        )}
        <div style={{ padding: 'var(--space-4) var(--space-5)', overflowY: 'auto' }}>{children}</div>
        {footer && (
          <div
            style={{
              padding: 'var(--space-3) var(--space-5) var(--space-4)',
              borderTop: '1px solid var(--border-subtle)',
              display: 'flex',
              gap: 'var(--space-2)',
              justifyContent: 'flex-end',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
